import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Alert,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Print as PrintIcon,
  Engineering as BuilderIcon,
  Person as ClientIcon,
  WarningAmber as WarningIcon,
  CheckCircleOutline as CheckIcon,
  InfoOutlined as InfoIcon,
  VisibilityOff as VisibilityOffIcon,
  AccessTime as TimeIcon,
  GroupWork as EffortIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

import { SoWData } from '../services/sowService';

interface SoWDisplayProps {
  data: SoWData | null;
  projectTitle?: string;
  showActions?: boolean;
}

const SoWDisplay: React.FC<SoWDisplayProps> = ({
  data,
  projectTitle,
  showActions = true,
}) => {
  const theme = useTheme();

  if (!data) return <Alert severity="info">Scope of Work not found.</Alert>;
  if (!data.tender_document) return <Alert severity="warning">Building Works Specification is missing.</Alert>;

  const { tender_document, internal_estimates, generatedAt } = data;

  // --- PRINT GENERATOR (Bill of Quantities Style) ---
  const handlePrint = () => {
    const printWindow = window.open('', '', 'width=1000,height=900');
    if (!printWindow) return;

    // We build a completely separate, high-density HTML structure for the builder
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Building Works Specification</title>
          <style>
            /* Reset & Typography */
            body { font-family: 'Arial', sans-serif; font-size: 10pt; color: #000; line-height: 1.3; }
            
            /* --- PAGE SETUP --- */
            /* 
               1. margin: 0 hides the default browser header/footer (removing 'about:blank').
               2. We add padding to body to create our own document margins.
            */
            @page {
              size: A4;
              margin: 0; 
            }

            body {
              /* Document Content Margins (approx 15mm) */
              padding: 15mm 15mm 20mm 15mm; 
              margin: 0;
            }

            /* --- CUSTOM FOOTER --- */
            /* This replaces the browser footer. position: fixed puts it on EVERY printed page. */
            .fixed-footer {
              position: fixed;
              bottom: 0;
              left: 15mm;
              right: 15mm;
              height: 10mm;
              border-top: 1px solid #ccc;
              padding-top: 5px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 8pt;
              color: #777;
              background-color: white; /* Cover content flowing behind it */
              z-index: 100;
            }

            /* Layout */
            .header-container { 
              border-bottom: 2px solid #000; 
              margin-bottom: 20px; 
              padding-bottom: 10px; 
              display: flex; 
              justify-content: space-between; 
              align-items: flex-end; 
            }
            .header-left { flex: 1; padding-right: 20px; }
            .header-right { text-align: right; display: flex; flex-direction: column; align-items: flex-end; }
            
            /* Logo Styling */
            .logo-img { height: 50px; width: auto; object-fit: contain; margin-bottom: 2px; }
            /* Tagline under logo */
            .brand-sub { font-size: 8pt; color: #666; text-transform: uppercase; letter-spacing: 1px; font-weight: bold; }

            h1 { font-size: 20pt; margin: 0 0 5px 0; color: #2c3e50; }
            h2 { font-size: 14pt; margin-top: 25px; margin-bottom: 15px; background-color: #f5f5f5; padding: 6px; border-left: 5px solid #2c3e50; page-break-after: avoid; }
            h3 { font-size: 11pt; margin-top: 15px; margin-bottom: 8px; font-weight: bold; color: #333; page-break-after: avoid; }
            
            /* Constraints & Info Boxes */
            .box { padding: 10px; margin-bottom: 15px; border: 1px solid #ccc; }
            .constraints { border-color: #d32f2f; background-color: #fff5f5; }
            .constraints strong { color: #d32f2f; }
            
            .exclusions-block { 
              margin-top: 5px; 
              margin-bottom: 15px; 
              padding: 6px 10px; 
              background-color: #fafafa; 
              border-left: 3px solid #d32f2f; 
              font-size: 9pt; 
              color: #444;
              page-break-inside: avoid;
            }

            /* Dense Tables */
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 9.5pt; page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            th { background-color: #f2f2f2; border: 1px solid #999; padding: 6px; text-align: left; font-weight: bold; color: #333; }
            td { border: 1px solid #ccc; padding: 6px; vertical-align: top; }
            
            /* Helpers */
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .bold { font-weight: bold; }
            .notes { font-style: italic; color: #555; font-size: 9pt; }
            
            /* Supply Badges */
            .b-supply { font-weight: bold; color: #1b5e20; }
            .c-supply { font-weight: bold; color: #e65100; }

            /* Page Control */
            .page-break { page-break-before: always; }
            
            @media print {
              .header-container { display: flex !important; }
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .fixed-footer { position: fixed; bottom: 0; }
            }
          </style>
        </head>
        <body>
          <!-- Fixed Footer on Every Page -->
          <div class="fixed-footer">
            <span><strong>Quorite.com</strong></span>
            <span>Professional Tender Documentation</span>
          </div>

          <!-- Document Header (Page 1 Only) -->
          <div class="header-container">
            <div class="header-left">
              <h1>Building Works Specification</h1>
              <div style="margin-bottom: 4px;"><strong>Project:</strong> ${projectTitle || 'Home Renovation'}</div>
              <div><strong>Date:</strong> ${generatedAt ? new Date(generatedAt).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}</div>
            </div>
            <div class="header-right">
               <!-- LOGO Only - Tagline below -->
               <img src="/logo.png" alt="Quorite" class="logo-img" onerror="this.style.display='none'; this.nextElementSibling.innerText='Quorite.com';" />
               <div class="brand-sub">Professional Tender Docs</div>
            </div>
          </div>

          <!-- 1. CRITICAL CONSTRAINTS -->
          <div class="box constraints">
            <div style="margin-bottom: 8px;">
              <strong>SITE ACCESS & LOGISTICS:</strong><br/>
              ${tender_document.project_summary.site_access_constraints || 'N/A'}
            </div>
            <div>
              <strong>OCCUPANCY:</strong> ${tender_document.project_summary.occupancy_status || 'N/A'}
            </div>
          </div>

          <p><strong>Description:</strong> ${tender_document.project_summary.description}</p>

          <!-- 2. COMPLIANCE -->
          ${tender_document.compliance_requirements.length > 0 ? `
            <h2>1.0 Compliance & Certification</h2>
            <table>
              <thead>
                <tr>
                  <th width="30%">Certificate</th>
                  <th width="70%">Requirement / Trigger</th>
                </tr>
              </thead>
              <tbody>
                ${tender_document.compliance_requirements.map(req => `
                  <tr>
                    <td class="bold">${req.certificate}</td>
                    <td>${req.trigger}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : ''}

          <!-- 3. SCOPE OF WORKS (DENSE) -->
          <h2>2.0 Detailed Scope of Works</h2>
          <p style="margin-bottom: 15px;"><em>Pricing Notes: Please price all items marked "Builder Supply". For "Client Supply" items, price installation labor only.</em></p>
          
          ${tender_document.scope_of_works.map(phase => `
            <h3>${phase.phase_name}</h3>
            <table>
              <thead>
                <tr>
                  <th width="60%">Description of Works</th>
                  <th width="15%" class="text-center">Quantity</th>
                  <th width="25%">Notes</th>
                </tr>
              </thead>
              <tbody>
                ${phase.line_items.map(item => `
                  <tr>
                    <td>${item.description}</td>
                    <td class="text-center bold">${item.quantity} ${item.quantifiable_unit}</td>
                    <td class="notes">${item.pricing_note || ''}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            ${phase.exclusions && phase.exclusions.length > 0 ? `
              <div class="exclusions-block">
                <span style="color: #d32f2f; font-weight: bold;">Specific Exclusions:</span> 
                ${phase.exclusions.join(', ')}
              </div>
            ` : ''}
          `).join('')}

          <div class="page-break"></div>

          <!-- 4. MATERIAL SCHEDULE (SUMMARY) -->
          <h2>3.0 Consolidated Material Schedule</h2>
          <table>
            <thead>
              <tr>
                <th width="20%">Category</th>
                <th width="30%">Item</th>
                <th width="10%" class="text-center">Qty</th>
                <th width="15%">Supply By</th>
                <th width="25%">Notes</th>
              </tr>
            </thead>
            <tbody>
              ${tender_document.material_schedule.map(mat => `
                <tr>
                  <td>${mat.category}</td>
                  <td class="bold">${mat.item_name}</td>
                  <td class="text-center">${mat.quantity} ${mat.unit}</td>
                  <td class="${mat.supply_responsibility === 'Client' ? 'c-supply' : 'b-supply'}">
                    ${mat.supply_responsibility === 'Client' ? 'CLIENT' : 'BUILDER'}
                  </td>
                  <td class="notes">${mat.notes || ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    
    // Timeout allows images/styles to load before print dialog opens
    setTimeout(() => {
      printWindow.print();
    }, 800);
  };

  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 2 }}>
      
      {/* --- ACTIONS BAR --- */}
      {showActions && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'grey.50', border: 1, borderColor: 'divider' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Tender Package Ready</Typography>
            <Typography variant="body2" color="text.secondary">Review the document below before sending to builders.</Typography>
          </Box>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
            Print / Save as PDF
          </Button>
        </Paper>
      )}

      {/* --- INTERNAL ESTIMATES (Homeowner Dashboard View) --- */}
      {internal_estimates && (
        <Paper 
          elevation={0}
          sx={{ 
            mb: 5, 
            p: 3, 
            bgcolor: '#fff8e1', 
            border: '1px dashed #ffb300',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <VisibilityOffIcon sx={{ color: '#f57f17' }} />
            <Typography variant="h6" sx={{ color: '#f57f17', fontWeight: 'bold' }}>
              Internal Estimates (Private)
            </Typography>
          </Box>
          <Typography variant="body2" paragraph sx={{ color: '#bf360c' }}>
            <strong>DO NOT SHARE WITH BUILDERS.</strong> These are AI-generated benchmarks to help you evaluate if quotes are fair.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ bgcolor: 'white' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TimeIcon color="primary" fontSize="large" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase">Estimated Duration</Typography>
                    <Typography variant="h5" fontWeight="bold">{internal_estimates.project_duration_weeks_range}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ bgcolor: 'white' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <EffortIcon color="secondary" fontSize="large" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase">Total Effort</Typography>
                    <Typography variant="h5" fontWeight="bold">{internal_estimates.total_person_days} Person Days</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Collapsible Breakdown */}
          <Accordion sx={{ mt: 2, bgcolor: 'transparent', boxShadow: 'none', border: '1px solid rgba(0,0,0,0.1)' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold" color="text.secondary">View Phase Breakdown</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Phase</TableCell>
                    <TableCell>Est. Days</TableCell>
                    <TableCell>Rationale</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {internal_estimates.phase_breakdowns.map((phase, i) => (
                    <TableRow key={i}>
                      <TableCell>{phase.phase_name}</TableCell>
                      <TableCell>{phase.estimated_person_days}</TableCell>
                      <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{phase.rationale}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        </Paper>
      )}

      {/* --- PREVIEW OF TENDER DOC (On Screen) --- */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 4 }}>
            <Typography variant="h4" fontWeight="bold" color="primary.main">{tender_document.title}</Typography>
            {projectTitle && <Typography variant="subtitle1" color="text.secondary">{projectTitle}</Typography>}
          </Box>

          {/* 1. Overview */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" gutterBottom sx={{ bgcolor: 'grey.100', p: 1, borderLeft: 4, borderColor: 'primary.main' }}>
              1.0 Project Overview
            </Typography>
            <Typography paragraph>{tender_document.project_summary.description}</Typography>
            
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">Site Constraints</Typography>
              <Typography variant="body2">{tender_document.project_summary.site_access_constraints}</Typography>
            </Alert>
          </Box>

          {/* 2. Scope */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" gutterBottom sx={{ bgcolor: 'grey.100', p: 1, borderLeft: 4, borderColor: 'primary.main' }}>
              2.0 Detailed Scope of Works
            </Typography>
            {tender_document.scope_of_works.map((phase, idx) => (
              <Box key={idx} sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                  {phase.phase_name}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell width="60%">Work Item</TableCell>
                        <TableCell width="15%">Qty</TableCell>
                        <TableCell width="25%">Pricing Note</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {phase.line_items.map((line, lIdx) => (
                        <TableRow key={lIdx}>
                          <TableCell>{line.description}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{line.quantity} {line.quantifiable_unit}</TableCell>
                          <TableCell sx={{ fontStyle: 'italic', color: 'text.secondary' }}>{line.pricing_note}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </Box>

          {/* 3. Materials */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ bgcolor: 'grey.100', p: 1, borderLeft: 4, borderColor: 'primary.main' }}>
              3.0 Material Schedule
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell>Category</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Supply</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tender_document.material_schedule.map((mat, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{mat.category}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{mat.item_name}</TableCell>
                      <TableCell>{mat.quantity} {mat.unit}</TableCell>
                      <TableCell>
                        <Chip 
                          label={mat.supply_responsibility === 'Client' ? "CLIENT" : "BUILDER"} 
                          size="small" 
                          color={mat.supply_responsibility === 'Client' ? "warning" : "success"} 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>{mat.notes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

        </CardContent>
      </Card>
    </Box>
  );
};

export default SoWDisplay;